import { Button, Card, Flex, Space, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router-dom"

import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { type RootState } from "../../../store.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import { cancelSelfDeletion } from "../redux/deletion-lock.thunks.ts"

const { Title, Text, Paragraph } = Typography

const mapStateToProps = (state: RootState) => ({
  status: state.selfDeletion.status,
  reasonId: state.selfDeletion.reason_id,
  createdAt: state.selfDeletion.created_at,
  cancelling: state.selfDeletion.cancelling
})

const mapDispatchToProps = {
  cancel: cancelSelfDeletion,
  signOut: logout
}

const connector = connect(mapStateToProps, mapDispatchToProps)

const AccountDeletionLockPage = ({
  status,
  reasonId,
  createdAt,
  cancelling,
  cancel,
  signOut
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()
  const navigate = useNavigate()

  // Cancelling is only possible while `pending`; once an admin approved,
  // anonymization is already running and the backend answers 409.
  const canCancel = status === "pending"

  return (
    <Flex
      align="center"
      justify="center"
      className="min-h-svh w-full bg-gray-50 p-4"
    >
      <Card className="w-full max-w-xl">
        <Title level={4}>
          {canCancel
            ? t("accountDeletionLock.pending.title")
            : t("accountDeletionLock.processing.title")}
        </Title>
        <Paragraph type="secondary">
          {canCancel
            ? t("accountDeletionLock.pending.explanation")
            : t("accountDeletionLock.processing.explanation")}
        </Paragraph>

        {(reasonId || createdAt) && (
          <Card size="small" className="mb-4">
            {!!reasonId && (
              <div className="mb-2">
                <Text type="secondary" className="block">
                  {t("accountDeletionLock.reasonLabel")}
                </Text>
                <Text>{t(`accountDeletionRequests.reasons.${reasonId}`)}</Text>
              </div>
            )}
            {!!createdAt && (
              <div>
                <Text type="secondary" className="block">
                  {t("accountDeletionLock.submittedLabel")}
                </Text>
                <Text>{String(formatDate(createdAt))}</Text>
              </div>
            )}
          </Card>
        )}

        {canCancel && (
          <Paragraph type="secondary">
            {t("accountDeletionLock.cancelHint")}
          </Paragraph>
        )}

        <Space>
          {canCancel && (
            <Button
              type="primary"
              loading={cancelling}
              onClick={() =>
                cancel({ successMessage: t("accountDeletionLock.cancelled") })
              }
            >
              {t("accountDeletionLock.cancelAction")}
            </Button>
          )}
          <Button onClick={() => signOut({ navigate })}>
            {t("accountDeletionLock.logoutAction")}
          </Button>
        </Space>
      </Card>
    </Flex>
  )
}

export default connector(AccountDeletionLockPage)
